import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Select, TableRowActionMenu } from "../components/ui";
import axios from "../utils/axios";
import { fetchCampaigns } from "../utils";
import { bucketProspectsIntoPipelineStages } from "../utils/campaignPipeline";
import { ownerSelector } from "../store/store";

const PIPELINE_CAMPAIGN_STATUSES = new Set(["ACTIVE", "PAUSED", "SCHEDULED"]);

const isPipelineEligibleCampaign = (campaign: { campaignStatus?: string }) =>
  PIPELINE_CAMPAIGN_STATUSES.has(String(campaign.campaignStatus || "").trim().toUpperCase());

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const PipelineOverview = () => {
  const navigate = useNavigate();
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [campaignOptions, setCampaignOptions] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingPipeline, setLoadingPipeline] = useState(false);
  const [listLabel, setListLabel] = useState("");
  const [pipelineStages, setPipelineStages] = useState<
    ReturnType<typeof bucketProspectsIntoPipelineStages>
  >([]);

  useEffect(() => {
    let active = true;

    const loadCampaigns = async () => {
      const ownerId = owner?._id ?? currentWorkspace?._id;
      if (!ownerId) {
        setCampaignOptions([]);
        setCampaignFilter("");
        return;
      }

      setLoadingCampaigns(true);
      try {
        const campaigns = await fetchCampaigns(
          ownerId,
          currentUser?.userId,
          currentWorkspace?._id
        );
        if (!active) return;

        const options: Array<{ value: string; label: string }> = (campaigns || [])
          .filter(isPipelineEligibleCampaign)
          .map((campaign: { _id: string; campaignName?: string }) => ({
            value: String(campaign._id),
            label: campaign.campaignName || "Untitled campaign",
          }));
        setCampaignOptions(options);
        setCampaignFilter((prev) => {
          if (prev && options.some((opt) => opt.value === prev)) return prev;
          return options[0]?.value || "";
        });
      } catch {
        if (active) {
          setCampaignOptions([]);
          setCampaignFilter("");
        }
      } finally {
        if (active) setLoadingCampaigns(false);
      }
    };

    loadCampaigns();
    return () => {
      active = false;
    };
  }, [owner?._id, currentWorkspace?._id, currentUser?.userId]);

  useEffect(() => {
    let active = true;

    const loadPipeline = async () => {
      if (!campaignFilter || (!owner?._id && !currentWorkspace?._id)) {
        setPipelineStages([]);
        setListLabel("");
        return;
      }

      setLoadingPipeline(true);
      try {
        const { data } = await axios.post("/campaign/getCampaignData", {
          ownerId: owner?._id,
          userId: currentUser?.userId,
          workspaceId: currentWorkspace?._id,
          campaignId: campaignFilter,
        });

        if (!active) return;

        const campaign = data?.campaign || {};
        const actions = Array.isArray(campaign.actions) ? campaign.actions : [];
        const prospects = (
          Array.isArray(campaign.prospectsWiseCampaignActions)
            ? campaign.prospectsWiseCampaignActions
            : []
        ).filter(Boolean);

        const resolvedListLabel =
          campaign.listId?.listName ||
          campaign.listIds?.[0]?.listName ||
          campaign.campaignName ||
          "Campaign list";

        setListLabel(resolvedListLabel);
        setPipelineStages(
          bucketProspectsIntoPipelineStages(prospects, actions, resolvedListLabel)
        );
      } catch {
        if (active) {
          setPipelineStages([]);
          setListLabel("");
        }
      } finally {
        if (active) setLoadingPipeline(false);
      }
    };

    loadPipeline();
    return () => {
      active = false;
    };
  }, [campaignFilter, owner?._id, currentWorkspace?._id, currentUser?.userId]);

  const loading = loadingCampaigns || loadingPipeline;

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-background-grey p-3 sm:p-4">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-4 sm:mb-5">
          <h1 className="text-xl font-semibold tracking-tight text-dark-black sm:text-h5">
            Pipeline overview
          </h1>
          <p className="mt-1 text-sm text-dark-grey">
            Choose a campaign to see prospect progress and outcomes for that campaign.
          </p>
        </header>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <Select
            variant="compact"
            size="sm"
            ariaLabel="Filter by campaign"
            value={campaignFilter}
            onChange={setCampaignFilter}
            disabled={loadingCampaigns || campaignOptions.length === 0}
            options={
              campaignOptions.length
                ? campaignOptions
                : [{ value: "", label: "No active, paused, or scheduled campaigns" }]
            }
            className="w-full min-w-0 sm:min-w-[200px] sm:max-w-md"
          />
          <span className="text-sm font-medium text-dark-grey">Campaign board</span>
        </div>

        <div className="mb-4 flex flex-col gap-2 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px] sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <p className="text-sm font-semibold text-dark-black">Campaign-specific pipeline</p>
          <p className="text-xs text-dark-grey">
            Select another campaign above to load its board. Columns follow your campaign sequence
            steps{listLabel ? ` · ${listLabel}` : ""}.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-dark-grey">Loading pipeline…</p>
        ) : !campaignFilter ? (
          <p className="text-sm text-dark-grey">Select a campaign to view the pipeline.</p>
        ) : (
          <>
            <p className="mb-2 text-[10px] text-dark-grey md:hidden">
              Swipe horizontally to see all pipeline columns.
            </p>
            <div className="-mx-3 overflow-x-auto px-3 pb-2 sm:-mx-4 sm:px-4 md:mx-0 md:overflow-visible md:px-0 [scrollbar-width:thin]">
              <div className="flex min-w-min snap-x snap-mandatory gap-2.5 md:grid md:auto-cols-[minmax(190px,1fr)] md:grid-flow-col md:snap-none">
                {pipelineStages.map((stage) => (
                  <section
                    key={stage.key}
                    className="flex w-[min(88vw,280px)] shrink-0 snap-start flex-col rounded-[8px] bg-bg-grey p-2.5 sm:w-[min(72vw,300px)] md:min-h-[360px] md:w-auto md:min-w-[190px] md:max-w-none"
                  >
                    <header className="mb-3 mt-0.5 flex items-center justify-between gap-2 text-sm font-semibold text-dark-black">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <i
                          className={`inline-block h-2 w-2 shrink-0 rounded-full ${stage.dotClass}`}
                        />
                        <span className="truncate">{stage.label}</span>
                      </span>
                      <b className="shrink-0 font-semibold">{stage.deals.length}</b>
                    </header>
                    <div className="min-h-[200px] flex-1 md:min-h-0">
                      {stage.deals.length > 0 ? (
                        stage.deals.map((deal) => (
                          <article
                            key={`${stage.key}-${deal.id}`}
                            className="mb-2 rounded-[7px] border border-light-grey bg-white p-2.5 text-sm"
                          >
                            <div className="mb-2 flex items-center justify-between gap-1">
                              <span className="grid h-[21px] w-[21px] shrink-0 place-items-center rounded-full bg-bg-light-blue text-[8px] font-semibold text-dark-blue">
                                {initials(deal.name)}
                              </span>
                              {campaignFilter ? (
                                <div
                                  className="-mr-1 shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <TableRowActionMenu
                                    viewDetailsLabel="View campaign"
                                    onViewDetails={() =>
                                      navigate(`/campaign-management/view/${campaignFilter}`)
                                    }
                                  />
                                </div>
                              ) : null}
                            </div>
                            <p className="truncate font-semibold text-dark-black">{deal.name}</p>
                            <p className="mt-0.5 line-clamp-2 text-[10px] text-dark-grey">
                              {deal.company} · {deal.role}
                            </p>
                            <div className="mt-3 flex items-center justify-between gap-2 text-[9px] text-dark-grey">
                              <span className="min-w-0 truncate">{deal.list}</span>
                              <span className="shrink-0">
                                {deal.hoursAgo > 0 ? `${deal.hoursAgo}h ago` : "—"}
                              </span>
                            </div>
                          </article>
                        ))
                      ) : (
                        <div className="rounded-[6px] border border-dashed border-light-grey px-2 py-4 text-center text-[11px] text-dark-grey">
                          No prospects
                        </div>
                      )}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PipelineOverview;
